// "Best on a desktop": the amber note on heavy tools, the dialog that stands in for a wizard on a phone, and the HOC that applies it.
import { Monitor } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIsPhone } from "@/hooks/useIsPhone";

export function DesktopOnlyNote({ tool = "This tool", className = "" }) {
  return (
    <div className={`flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] leading-snug text-amber-900 ${className}`} data-testid="desktop-only-note">
      <Monitor className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{tool} is built for a desktop screen — it works here (scroll sideways where needed) but is easier on a laptop.</span>
    </div>
  );
}

export function DesktopOnlyDialog({ title = "This wizard", onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent aria-describedby={undefined} data-testid="desktop-only-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 text-base pr-8"><Monitor className="w-4 h-4 text-amber-700" />{title} needs a desktop</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">{title} has several steps and previews that need a wider screen. Open this file on your laptop to run it — everything else on the file works here.</p>
        <Button onClick={onClose} className="w-full h-11" data-testid="desktop-only-dialog-close">OK</Button>
      </DialogContent>
    </Dialog>
  );
}

export function desktopOnly(Component, title) {
  function Guarded(props) {
    const isPhone = useIsPhone();
    if (!isPhone) return <Component {...props} />;
    if (props.open === false) return null;
    const close = () => { props.onClose?.(); props.onOpenChange?.(false); };
    return <DesktopOnlyDialog title={title} onClose={close} />;
  }
  Guarded.displayName = `DesktopOnly(${Component.displayName || Component.name || "Wizard"})`;
  return Guarded;
}
